import type { BidResponse } from '../../api/endpoints/admin';

/**
 * Read-only figures for the award confirmation (#248). Every amount comes from
 * the server's bid snapshot. The margin uses the server's `marginAmount` /
 * `effectiveMarginPct` (#221) so a Fixed shipper price shows its real margin;
 * an older API without them falls back to the stored pct and the subtraction.
 */
export interface AwardSummaryData {
  carrierGets: number;
  /** null on a legacy bid without a shipper-facing figure: the row is hidden. */
  shipperPays: number | null;
  marginRupees: number | null;
  /** "5.00%", or null when the bid has no margin snapshot. */
  marginPct: string | null;
  /** IST "DD Mon YYYY, HH:mm", or null when the bidder proposed none. */
  pickup: string | null;
  note: string | null;
  /** Other open bids of the current posting cycle that the award rejects. */
  otherOpenBids: number;
}

const OPEN_BID_STATUSES = new Set(['POSTED', 'PENDING']);

export function formatIstDayMonTime(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export function buildAwardSummary(
  bid: BidResponse,
  bids: BidResponse[],
  currentPostingCycle: number | null | undefined,
): AwardSummaryData {
  const shipperPays = bid.shipperFacingAmount ?? null;
  const pct = bid.effectiveMarginPct ?? bid.shipperMarginPct;
  const note = bid.notes?.trim() || null;
  const otherOpenBids = bids.filter(
    (b) =>
      b.id !== bid.id &&
      OPEN_BID_STATUSES.has(b.status) &&
      (currentPostingCycle == null ||
        b.postingCycle == null ||
        b.postingCycle === currentPostingCycle),
  ).length;
  return {
    carrierGets: bid.amount,
    shipperPays,
    marginRupees: shipperPays != null ? (bid.marginAmount ?? shipperPays - bid.amount) : null,
    marginPct: shipperPays != null && pct != null ? `${Number(pct).toFixed(2)}%` : null,
    pickup: bid.proposedPickupTime ? formatIstDayMonTime(bid.proposedPickupTime) : null,
    note,
    otherOpenBids,
  };
}
